import { AppLoaderOverlay } from '@flobi/asset-library/app-shell';
import { lazy, Suspense, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { AuthGate } from './auth/AuthGate';
import { Home } from './home/Home';
import { Tips } from './home/Tips';
import { RenderLab } from './lab/RenderLab';
import { ShellLab } from './lab/ShellLab';
import { EffectsLab } from './lab/EffectsLab';
import { LayoutsLab } from './lab/Layouts';
import { DashboardsLab } from './lab/Dashboards';
import { GuidesLab } from './lab/GuidesLab';
import { EditorPage } from './editor/EditorPage';
import { ExportRoute } from './export/ExportRoute';
import { NotFound } from './NotFound';
import { LayersImport, ReceiveRoute } from './layers/LayersImport';
import { ArtworkNotifications } from './notifications/ArtworkNotifications';
import { ArtworkSupport } from './support/ArtworkSupport';
import { ArtworkHints } from './lib/hints/ArtworkHintsHost';
import { ArtworkTourHost } from './lib/tours/ArtworkTourHost';
import { ArtworkShareHost } from './share/ArtworkShareHost';
import { ArtworkProjects } from './projects/ArtworkProjects';
import { ViewPage } from './share/ViewPage';
import { ErrorBoundary } from './ui/ErrorBoundary';
import { TooltipLayer } from './ui/tooltip';
import { useKeyboardFocus } from './ui/keyboardFocus';
import { storedChoice, systemTheme } from './theme';
import ArtworkAnalyticsHost from "./lib/analytics/ArtworkAnalyticsHost";
import { SharkSidePanel } from "@flobi/asset-library/agent";
import "@flobi/asset-library/agent.css";
import { editorContext } from './editor/agentSelection';

/** The light or dark curtain, as the page will open: read once, as the overlay reads it. */
const curtainTheme = (() => {
  const choice = storedChoice();
  return choice === 'system' ? systemTheme() : choice;
})();

/** What the agent is told about where the person is, read when a message is sent: the file open, or nothing (home). */
const agentContext = () => editorContext(window.location.pathname);

/** Where the curtain is drawn: the signed-in pages, not the labs or the page for a bad link (review H3). */
const CURTAINED = (path: string) => path === '/' || path.startsWith('/f/');
const GenerationLab = import.meta.env.DEV ? lazy(() => import('./lab/GenerationLab').then(module => ({ default: module.GenerationLab }))) : null;
const ImageToolsLab = import.meta.env.DEV ? lazy(() => import('./lab/ImageToolsLab').then(module => ({ default: module.ImageToolsLab }))) : null;

export function App() {
  useKeyboardFocus();
  const { pathname } = useLocation();
  // Read once, as the overlay reads its props once: it is mounted for the cold load only.
  const [curtained] = useState(() => CURTAINED(pathname));
  // The export page is a render in progress, nothing else: no agent, no tips, no tours over it.
  const rendering = pathname.startsWith('/export/');
  return (
    <ErrorBoundary>
    {/* The platform's wordmark curtain, the one Home, Writer and Drive show while they open
        (Mohamad, 2026-10-05). Mounted once, above the routes; the sign-in gate raises it by
        rendering <AppLoader /> while the session resolves. Once-only, like Home's: it covers
        the cold load and then retires, taking its WebGL contexts and video with it (review H4). */}
    {curtained ? <AppLoaderOverlay app="artwork" theme={curtainTheme} /> : null}
    <TooltipLayer />
    {/* Flobi Support (support/): the panel opens over any page, the editor included. */}
    <ArtworkSupport>
    {/* The one notification system (notifications/): the banners and the Centre open over any page,
        the editor included; the bells in Home's header and the editor's top bar open it. */}
    <ArtworkNotifications>
    {/* Tips as a corner card (lib/hints/): asleep until signed in, then over Home and the editor. */}
    {rendering ? null : <ArtworkHints />}
    {rendering ? null : <ArtworkTourHost />}
    <ArtworkAnalyticsHost />
    {/* Shark in the side panel (W1 pass 3): the old agent panel's trigger opens it; turns go to this app's agent. */}
    {rendering ? null : <SharkSidePanel app="artwork" gateway={import.meta.env.VITE_BACKEND_URL || import.meta.env.VITE_API_URL || ''} getContext={agentContext} />}
    <Routes>
      {/* A file's "Anyone with the link": its pages as the renderer draws them, for people
          with no account — so outside sign-in. The token in the address is the credential. */}
      <Route path="/v/:token" element={<ViewPage />} />
      {/* The rendering lab talks to no backend, so it stays outside sign-in: the
          benchmark drives it in a browser with no session. */}
      <Route path="/lab/render" element={<RenderLab />} />
      {/* The editor's chrome, as a picture rather than a feature: no backend, nothing to
          sign in to, nothing to save — so it can be opened, screenshotted and argued
          about without an account. Development builds only. */}
      {import.meta.env.DEV ? <Route path="/lab/shell" element={<ShellLab />} /> : null}
      {/* The selection bar's Effects popover, five ways (16 Oct). */}
      {import.meta.env.DEV ? <Route path="/lab/effects" element={<EffectsLab />} /> : null}
      {/* Four ways the editor could be laid out and worked, clickable, side by side. */}
      {import.meta.env.DEV ? <Route path="/lab/layouts" element={<LayoutsLab />} /> : null}
      {/* Six ways the home (the file browser) could be laid out, over a made-up workspace. */}
      {import.meta.env.DEV ? <Route path="/lab/dashboards" element={<DashboardsLab />} /> : null}
      {/* Every tool guide's picture and cursor, to review them together. */}
      {import.meta.env.DEV ? <Route path="/lab/guides" element={<GuidesLab />} /> : null}
      {GenerationLab ? <Route path="/lab/generation" element={<Suspense fallback={null}><GenerationLab /></Suspense>} /> : null}
      {ImageToolsLab ? <Route path="/lab/image-tools" element={<Suspense fallback={null}><ImageToolsLab /></Suspense>} /> : null}
      {/* The signed-in pages: one sign-in gate, one Projects provider (projects/, the shared kit in
          Artwork's look) and the one Share / Invite mount (share/), whose Move to Project reads the
          kit's list and asks the kit's move question. */}
      <Route element={<SignedIn />}>
        <Route path="/" element={<Home />} />
        {/* Tips: every hint, by what it helps with; Try it opens a file and plays it there. */}
        <Route path="/tips" element={<Tips />} />
        <Route path="/f/:documentId" element={<EditorRoute />} />
        <Route path="/export/:documentId" element={<ExportRoute />} />
        {/* A chat's layered file (Split to layers → Open in Artwork): a new file or one of yours, then the editor places it. */}
        <Route path="/layers" element={<LayersImport />} />
        {/* What another Flobi app sends (cross-app pages, 29 Oct): a layered picture opens as /layers does. */}
        <Route path="/receive" element={<ReceiveRoute />} />
      </Route>
      {/* The address Flobi's hub links a file by (notifications, Shared with me): the editor's,
          with whatever it asked for (?share=requests opens Share on who asked for access). */}
      <Route path="/file/:documentId" element={<FileLink />} />
      {/* Anything else is a link that goes nowhere, said as such (files-doc-F20). */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </ArtworkNotifications>
    </ArtworkSupport>
    </ErrorBoundary>
  );
}

/** Everything behind sign-in shares one gate and one Projects provider, kept across pages. */
function SignedIn() {
  return (
    <AuthGate>
      <ArtworkProjects>
        <ArtworkShareHost />
        <Outlet />
      </ArtworkProjects>
    </AuthGate>
  );
}

/**
 * One editor per file: the bell can lead from one file to another in place, and the page's
 * canvas, room and panels belong to the file they opened with. Keyed in lower case, as the
 * editor puts the address right without starting over. Kept as one instance across files,
 * Duplicate's jump to the copy carried the original's export, rename, Layers and uploads
 * into it (review E8).
 */
function EditorRoute() {
  const { documentId = '' } = useParams();
  // A file this person was just let into (the access screen's answer) opens afresh, in place.
  const [opening, setOpening] = useState(0);
  return <EditorPage key={`${documentId.toLowerCase()}:${opening}`} onReopen={() => setOpening((n) => n + 1)} />;
}

function FileLink() {
  const { documentId = '' } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/f/${encodeURIComponent(documentId)}${search}${hash}`} replace />;
}
